import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { movimientosAPI, clientesAPI, proveedoresAPI, recibosCobroAPI, recibosCobroGrupoAPI, pagosAPI, movimientosPredefinidosAPI, whatsappTemplatesAPI, usuariosAPI, whatsappBaileysAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { WhatsAppServicioPanel } from '../components/WhatsAppServicioPanel';
import { PlantillaImagenEditor } from '../components/PlantillaImagenEditor';
import {
  readUseBaileysFromStorage,
  writeUseBaileysToStorage,
  readSendRateProfileFromStorage,
  sendWhatsAppSequential,
  abrirWhatsappWebEscalonado,
} from '../utils/whatsappBaileys';
import { assertHorarioUsoCliente } from '../hooks/useHorariosUso';
import '../Movimientos.css';
import '../Clientes.css';

import { ActionIcon } from '../components/ActionIcon';
import { FacturaMovimientoMarca } from '../components/FacturaMovimientoMarca';
import { SelectorColumnas } from '../components/SelectorColumnas';
import { SelectorFiltros } from '../components/SelectorFiltros';
import { ExportActions } from '../components/ExportActions';
import { exportFromColumnas, valorCeldaMovimientoExport } from '../utils/exportListado';
import { sortMovimientosCronologico, deltaSaldoMovimiento } from '../utils/movimientosOrden';
import {
  imprimirComprobanteMovimiento,
  imprimirReporteMovimientos,
  movimientoTienePago,
  movimientoTieneRecibo,
  movimientoTieneReciboGrupo,
} from '../utils/impresionMovimientos';
import { useMovimientoDetalle } from '../hooks/useMovimientoDetalle';
import { MovimientoDetalleModal } from '../components/MovimientoDetalleModal';
import { MovimientoConceptoCell } from '../components/MovimientoConceptoCell';
import FiltroPredefinidosMultiselect from '../components/FiltroPredefinidosMultiselect';
import { useBranding } from '../context/BrandingContext';

const MESES_NOMBRES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatearTelefonoAbono = (telefono) => {
  if (!telefono) return '';
  let n = String(telefono).replace(/[\s\-()]/g, '');
  if (n.startsWith('0')) n = n.substring(1);
  if (!n.startsWith('+') && !n.startsWith('54')) n = '54' + n;
  if (!n.startsWith('+')) n = '+' + n;
  return n;
};

const aplicarPlantillaAbono = (cliente, texto, mesNombre, anio, concepto) => {
  const telefono = cliente.celular || cliente.telefono || '';
  const monto = Number(parseFloat(cliente.abono_mensual ?? cliente.valor_abono) || 0).toFixed(2);
  return texto
    .replace(/{razon_social}/g, cliente.razon_social || '')
    .replace(/{cuit}/g, cliente.cuit || '')
    .replace(/{telefono}/g, telefono)
    .replace(/{tipo_nombre}/g, cliente.tipo_nombre || '')
    .replace(/{mes}/g, mesNombre || '')
    .replace(/{anio}/g, String(anio || ''))
    .replace(/{monto}/g, monto)
    .replace(/{concepto}/g, concepto || '');
};

const CLAVE_PREFERENCIAS_MOV = {
  clientes: 'movimientos_clientes_listado',
  proveedores: 'movimientos_proveedores_listado',
};

const COLUMNAS_DEFINICION = [
  { id: 'fecha', label: 'Fecha' },
  { id: 'entidad', label: 'Cliente / Proveedor' },
  { id: 'concepto', label: 'Concepto' },
  { id: 'debito', label: 'Débito' },
  { id: 'credito', label: 'Crédito' },
  { id: 'saldo', label: 'Saldo acumulado' },
];

const defaultColumnasVisiblesMov = COLUMNAS_DEFINICION.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
const defaultFiltrosVisiblesMov = {
  entidad: true,
  tipo_movimiento: true,
  movimiento_predefinido: true,
  fecha: true,
};
const defaultOrdenColumnasMov = COLUMNAS_DEFINICION.map(c => c.id);

const FILTROS_DEFINICION_MOV = [
  { id: 'entidad', label: 'Entidad' },
  { id: 'tipo_movimiento', label: 'Tipo de movimiento' },
  { id: 'movimiento_predefinido', label: 'Movimiento predefinido' },
  { id: 'fecha', label: 'Fecha (desde/hasta)' },
];

const getSortValueMov = (mov, colId) => {
  switch (colId) {
    case 'fecha':
      return new Date(mov.fecha || 0).getTime() || 0;
    case 'entidad':
      return (mov.razon_social || '').toString().toLowerCase();
    case 'concepto':
      return (mov.concepto || '').toString().toLowerCase();
    case 'debito':
      return mov.tipo_movimiento === 'DEBITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'credito':
      return mov.tipo_movimiento === 'CREDITO' ? (parseFloat(mov.monto) || 0) : 0;
    case 'saldo':
      return parseFloat(mov.saldo_acumulado || 0) || 0;
    default:
      return '';
  }
};

const defaultFiltrosMov = {
  cliente_id: '',
  proveedor_id: '',
  tipo_movimiento: '',
  movimiento_predefinido_ids: [],
  fecha_desde: '',
  fecha_hasta: '',
};

const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function normalizarFiltrosMovGuardados(parsed) {
  const base = { ...defaultFiltrosMov, ...parsed };
  if (parsed?.movimiento_predefinido_id && !parsed?.movimiento_predefinido_ids?.length) {
    const n = parseInt(parsed.movimiento_predefinido_id, 10);
    base.movimiento_predefinido_ids = Number.isFinite(n) && n > 0 ? [n] : [];
  }
  if (!Array.isArray(base.movimiento_predefinido_ids)) {
    base.movimiento_predefinido_ids = [];
  }
  delete base.movimiento_predefinido_id;
  return base;
}

const Movimientos = ({ mode = 'clientes' }) => {
  const { fechaHoy, formatFecha, formatFechaHora } = useZonaHoraria();
  const esProveedores = mode === 'proveedores';
  const storageKeyFiltros = esProveedores ? 'movimientos_proveedores_filtros' : 'movimientos_clientes_filtros';

  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Evita pantalla completa "Cargando..." en cada refetch por filtros. */
  const primeraCargaMovimientosRef = useRef(true);
  const [refrescandoMovimientos, setRefrescandoMovimientos] = useState(false);
  const cargaMovimientosSeqRef = useRef(0);
  const [filtros, setFiltros] = useState(() => {
    const prevPath = sessionStorage.getItem('app_prev_path') || '';
    if (prevPath && !prevPath.startsWith('/movimientos')) {
      sessionStorage.removeItem('movimientos_clientes_filtros');
      sessionStorage.removeItem('movimientos_proveedores_filtros');
    }
    const key = mode === 'proveedores' ? 'movimientos_proveedores_filtros' : 'movimientos_clientes_filtros';
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        return normalizarFiltrosMovGuardados(parsed);
      }
    } catch (e) {}
    return { ...defaultFiltrosMov };
  });
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [predefinidos, setPredefinidos] = useState([]);
  const [showAbonoMensualModal, setShowAbonoMensualModal] = useState(false);
  const [clientesAbono, setClientesAbono] = useState([]);
  const [clientesAbonoLoading, setClientesAbonoLoading] = useState(false);
  const [abonoMensualSeleccionados, setAbonoMensualSeleccionados] = useState([]);
  const [abonoMensualMes, setAbonoMensualMes] = useState(() => new Date().getMonth() + 1);
  const [abonoMensualAnio, setAbonoMensualAnio] = useState(() => new Date().getFullYear());
  const [abonoMensualPermitirDuplicado, setAbonoMensualPermitirDuplicado] = useState(false);
  const [abonoMensualAclaracion, setAbonoMensualAclaracion] = useState('');
  const [abonoMensualEnviando, setAbonoMensualEnviando] = useState(false);
  const [clientesCargadosAbono, setClientesCargadosAbono] = useState([]);
  const [showModalWhatsAppAbono, setShowModalWhatsAppAbono] = useState(false);
  const [abonoConcepto, setAbonoConcepto] = useState('');
  const [abonoMesNombre, setAbonoMesNombre] = useState('');
  const [abonoAnio, setAbonoAnio] = useState('');
  const [plantillasAbono, setPlantillasAbono] = useState([]);
  const [plantillaSeleccionadaIdAbono, setPlantillaSeleccionadaIdAbono] = useState('');
  const [mensajePersonalizadoAbono, setMensajePersonalizadoAbono] = useState('');
  const [waUseBaileys, setWaUseBaileys] = useState(() => readUseBaileysFromStorage());
  const [waSendRateProfile, setWaSendRateProfile] = useState(() => readSendRateProfileFromStorage());
  const [waStatus, setWaStatus] = useState(null);
  const [waStatusLoading, setWaStatusLoading] = useState(false);
  const [anulandoComprobante, setAnulandoComprobante] = useState(false);
  const {
    detalleMovimiento,
    detalleRecibo,
    detalleReciboGrupo,
    detallePago,
    detallePagoError,
    detalleLoading,
    abrirDetalle: abrirDetalleMovimiento,
    cerrarDetalle,
  } = useMovimientoDetalle();
  const { logoUrl } = useBranding();
  const [incluirSaldoInicial, setIncluirSaldoInicial] = useState(true);
  const [incluirSaldoTotal, setIncluirSaldoTotal] = useState(true);
  const [saldoInicial, setSaldoInicial] = useState(null);
  const [saldoTotal, setSaldoTotal] = useState(null);
  const [saldoFiltrado, setSaldoFiltrado] = useState(null);
  const [saldoCuenta, setSaldoCuenta] = useState(null);
  const { hasPermission } = useAuth();
  const [opHuerfanas, setOpHuerfanas] = useState([]);
  const [opHuerfanasLoading, setOpHuerfanasLoading] = useState(false);
  const [opHuerfanasVisible, setOpHuerfanasVisible] = useState(true);

  const [columnasVisibles, setColumnasVisibles] = useState(() => ({ ...defaultColumnasVisiblesMov }));
  const [filtrosVisibles, setFiltrosVisibles] = useState(() => ({ ...defaultFiltrosVisiblesMov }));
  const [ordenColumnas, setOrdenColumnas] = useState(() => [...defaultOrdenColumnasMov]);
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [, setLoadingPreferencias] = useState(true);
  const [savingPreferencias, setSavingPreferencias] = useState(false);

  const clavePreferenciasMov = esProveedores ? CLAVE_PREFERENCIAS_MOV.proveedores : CLAVE_PREFERENCIAS_MOV.clientes;

  useEffect(() => {
    sessionStorage.setItem(storageKeyFiltros, JSON.stringify(filtros));
  }, [filtros, storageKeyFiltros]);

  const fechaDefaultAplicadaRef = useRef(false);
  useEffect(() => {
    if (fechaDefaultAplicadaRef.current) return;
    fechaDefaultAplicadaRef.current = true;
    if (filtros.fecha_desde) return;
    setFiltros((prev) => ({ ...prev, fecha_desde: fechaHoy() }));
  }, [fechaHoy, filtros.fecha_desde]);

  const loadOpHuerfanas = useCallback(async () => {
    if (!esProveedores || !hasPermission('gestionar_op_huerfanas')) {
      setOpHuerfanas([]);
      return;
    }
    try {
      setOpHuerfanasLoading(true);
      const res = await movimientosAPI.getHuerfanosOp();
      if (res.success && Array.isArray(res.data)) {
        setOpHuerfanas(res.data);
      } else {
        setOpHuerfanas([]);
      }
    } catch (e) {
      console.error('Error al cargar OP huérfanas:', e);
      setOpHuerfanas([]);
    } finally {
      setOpHuerfanasLoading(false);
    }
  }, [esProveedores, hasPermission]);

  useEffect(() => {
    loadOpHuerfanas();
  }, [loadOpHuerfanas]);

  const handleAnularOpHuerfana = async (mov) => {
    if (!window.confirm(`¿Anular el débito huérfano de ${mov.proveedor_razon_social || 'proveedor'} por $${parseFloat(mov.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}? Se registrará un crédito compensatorio.`)) return;
    try {
      const res = await movimientosAPI.anularHuerfano(mov.id);
      if (res.success) {
        toast.success(res.message || 'Débito huérfano anulado');
        loadOpHuerfanas();
        loadMovimientos();
      } else {
        toast.error(res.message || 'No se pudo anular');
      }
    } catch (e) {
      toast.error(e.message || 'Error al anular débito huérfano');
    }
  };

  const colVisible = (id) => columnasVisibles[id] !== false;
  const filtroVisible = useCallback(
    (id) => filtrosVisibles[id] !== false,
    [filtrosVisibles]
  );

  const handleImprimirMovimiento = async (mov) => {
    try {
      const ok = await imprimirComprobanteMovimiento(mov, formatFecha, logoUrl);
      if (!ok) toast.error('No se pudo imprimir el comprobante');
    } catch (error) {
      toast.error(error.message || 'Error al imprimir');
    }
  };

  const abonoMensualPredefinido = predefinidos.find(
    (m) => m.descripcion === 'Abono Mensual' && m.tipo_movimiento === 'DEBITO'
  );

  const loadClientes = useCallback(async () => {
    try {
      const response = await clientesAPI.getAll({ activo: true });
      if (response.success) {
        setClientes(response.data);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  }, []);

  const loadProveedores = useCallback(async () => {
    try {
      const response = await proveedoresAPI.getAll({ activo: true });
      if (response.success) {
        setProveedores(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  }, []);

  const loadPredefinidos = useCallback(async () => {
    try {
      const params = { activo: true, para_filtro: '1' };
      if (esProveedores) params.uso = 'proveedores';
      else params.uso = 'clientes';
      const res = await movimientosPredefinidosAPI.getAll(params);
      if (res.success && Array.isArray(res.data)) setPredefinidos(res.data);
    } catch (e) {
      setPredefinidos([]);
    }
  }, [esProveedores]);

  const loadMovimientos = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaMovimientosRef.current;
    const seq = ++cargaMovimientosSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescandoMovimientos(true);
      const params = { tipo: esProveedores ? 'proveedores' : 'clientes', limit: 0 };
      if (esProveedores && filtros.proveedor_id) params.proveedor_id = filtros.proveedor_id;
      if (!esProveedores && filtros.cliente_id) params.cliente_id = filtros.cliente_id;
      if (filtros.tipo_movimiento) params.tipo_movimiento = filtros.tipo_movimiento;
      if (filtros.movimiento_predefinido_ids?.length) {
        params.movimiento_predefinido_ids = filtros.movimiento_predefinido_ids.join(',');
      }
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const hayFiltros =
        !!(filtros.fecha_desde || filtros.fecha_hasta || filtros.tipo_movimiento || filtros.movimiento_predefinido_ids?.length) ||
        (esProveedores ? !!filtros.proveedor_id : !!filtros.cliente_id);
      if (hayFiltros) {
        params.incluir_resumen_saldos = '1';
        if (filtros.fecha_desde && incluirSaldoInicial) params.incluir_saldo_inicial = '1';
      }

      const response = await movimientosAPI.getAll(params);
      if (seq !== cargaMovimientosSeqRef.current) return;
      if (response.success) {
        setMovimientos(Array.isArray(response.data) ? response.data : []);
        setSaldoInicial(response.saldo_inicial !== undefined ? response.saldo_inicial : null);
        setSaldoTotal(response.saldo_total !== undefined ? response.saldo_total : null);
        setSaldoFiltrado(response.saldo_filtrado !== undefined ? response.saldo_filtrado : null);
        setSaldoCuenta(response.saldo_cuenta !== undefined ? response.saldo_cuenta : null);
      } else {
        setMovimientos([]);
        setSaldoInicial(null);
        setSaldoTotal(null);
        setSaldoFiltrado(null);
        setSaldoCuenta(null);
      }
    } catch (error) {
      if (seq !== cargaMovimientosSeqRef.current) return;
      setMovimientos([]);
      setSaldoInicial(null);
      setSaldoTotal(null);
      setSaldoFiltrado(null);
      setSaldoCuenta(null);
      toast.error(error.message || 'Error al cargar movimientos');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaMovimientosRef.current = false;
      }
      if (seq === cargaMovimientosSeqRef.current) {
        setRefrescandoMovimientos(false);
      }
    }
  }, [filtros, incluirSaldoInicial, incluirSaldoTotal, esProveedores]);

  useEffect(() => {
    loadClientes();
    loadMovimientos();
    loadPredefinidos();
  }, [loadClientes, loadMovimientos, loadPredefinidos]);

  useEffect(() => {
    if (esProveedores) loadProveedores();
  }, [esProveedores, loadProveedores]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreferencias(true);
      try {
        const res = await usuariosAPI.getPreferencias(clavePreferenciasMov);
        if (cancelled) return;
        const raw = res.data;
        const data = raw?.valor ?? (typeof raw === 'object' && !Array.isArray(raw) ? raw : null);
        const parsed = data && typeof data === 'object' ? data : (data && typeof data === 'string' ? (() => { try { return JSON.parse(data); } catch (_) { return null; } })() : null);
        if (parsed) {
          if (parsed.columnasVisibles && typeof parsed.columnasVisibles === 'object') {
            setColumnasVisibles(prev => ({ ...defaultColumnasVisiblesMov, ...parsed.columnasVisibles }));
          }
          if (parsed.filtrosVisibles && typeof parsed.filtrosVisibles === 'object') {
            setFiltrosVisibles(prev => ({ ...defaultFiltrosVisiblesMov, ...parsed.filtrosVisibles }));
          }
          if (parsed.ordenColumnas && Array.isArray(parsed.ordenColumnas) && parsed.ordenColumnas.length) {
            setOrdenColumnas(parsed.ordenColumnas);
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Error al cargar preferencias movimientos:', e);
      } finally {
        if (!cancelled) setLoadingPreferencias(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clavePreferenciasMov]);

  const guardarPreferenciasListado = useCallback(async () => {
    setSavingPreferencias(true);
    try {
      await usuariosAPI.savePreferencias(clavePreferenciasMov, {
        columnasVisibles: { ...columnasVisibles },
        filtrosVisibles: { ...filtrosVisibles },
        ordenColumnas: [...ordenColumnas],
      });
      toast.success('Configuración guardada.');
    } catch (e) {
      toast.error(e.message || 'Error al guardar configuración');
    } finally {
      setSavingPreferencias(false);
    }
  }, [clavePreferenciasMov, columnasVisibles, filtrosVisibles, ordenColumnas]);

  const abrirModalAbonoMensual = useCallback(async () => {
    setShowAbonoMensualModal(true);
    setAbonoMensualSeleccionados([]);
    setAbonoMensualPermitirDuplicado(false);
    setAbonoMensualAclaracion('');
    setClientesAbonoLoading(true);
    try {
      const res = await clientesAPI.getAll({ activo: true });
      if (res.success) setClientesAbono(res.data);
    } catch (e) {
      toast.error('Error al cargar clientes');
    } finally {
      setClientesAbonoLoading(false);
    }
  }, []);

  const toggleClienteAbono = (id) => {
    setAbonoMensualSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const seleccionarTodosAbono = () => {
    const conValor = clientesAbono.filter((c) => parseFloat(c.abono_mensual ?? c.valor_abono) > 0).map((c) => c.id);
    setAbonoMensualSeleccionados(conValor);
  };

  const aplicarAbonoMensual = async () => {
    if (abonoMensualSeleccionados.length === 0) {
      toast.error('Seleccione al menos un cliente');
      return;
    }
    const mes = abonoMensualMes;
    const anio = abonoMensualAnio;
    const aclaracionTxt = abonoMensualAclaracion.trim();
    const permitirDuplicado = abonoMensualPermitirDuplicado;
        const hoy = fechaHoy();
        const fecha = hoy;
    setAbonoMensualEnviando(true);
    try {
      const response = await movimientosAPI.createAbonoMensual({
        cliente_ids: abonoMensualSeleccionados,
        mes,
        anio,
        fecha,
        movimiento_predefinido_id: abonoMensualPredefinido?.id || null,
        permitir_duplicado: permitirDuplicado,
        aclaracion: aclaracionTxt || undefined,
      });
      if (response.success) {
        toast.success(response.message);
        setShowAbonoMensualModal(false);
        setAbonoMensualPermitirDuplicado(false);
        setAbonoMensualAclaracion('');
        loadMovimientos();
        const data = response.data || response;
        const cargados = Array.isArray(data.clientes_cargados)
          ? data.clientes_cargados
          : Array.isArray(data.clientesCargados)
            ? data.clientesCargados
            : [];
        if (cargados.length > 0) {
          const conceptoPreview = aclaracionTxt
            ? `Abono Mensual - ${MESES_NOMBRES[mes - 1]} ${anio} - ${aclaracionTxt}`
            : `Abono Mensual - ${MESES_NOMBRES[mes - 1]} ${anio}`;
          setClientesCargadosAbono(cargados);
          setAbonoConcepto(data.concepto || conceptoPreview);
          setAbonoMesNombre(MESES_NOMBRES[mes - 1] || '');
          setAbonoAnio(data.anio != null ? data.anio : anio);
          setMensajePersonalizadoAbono(
            'Hola {razon_social},\n\nSe ha registrado el abono correspondiente a {mes} {anio}.\nConcepto: {concepto}\nMonto: $' +
              '{monto}\nTipo: {tipo_nombre}\n\nVariables: razon_social, cuit, telefono, tipo_nombre, mes, anio, monto, concepto.'
          );
          setShowModalWhatsAppAbono(true);
          try {
            const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
            if (res.success && Array.isArray(res.data) && res.data.length > 0) {
              setPlantillasAbono(res.data);
              setPlantillaSeleccionadaIdAbono(res.data[0].id);
              setMensajePersonalizadoAbono(res.data[0].texto || '');
            }
          } catch (e) {
            setMensajePersonalizadoAbono(
              `Hola {razon_social},\n\nAbono {mes} {anio} registrado. Monto: \${monto}. Concepto: {concepto}.`
            );
          }
        }
      }
    } catch (error) {
      toast.error(error.message || 'Error al aplicar abono mensual');
    } finally {
      setAbonoMensualEnviando(false);
    }
  };

  const clientesAbonoConTelefono = clientesCargadosAbono.filter((c) => c.telefono || c.celular);

  const loadPlantillasAbono = useCallback(async () => {
    try {
      const res = await whatsappTemplatesAPI.getAll({ tipo: 'cliente' });
      if (res.success && Array.isArray(res.data)) setPlantillasAbono(res.data);
    } catch (e) {
      setPlantillasAbono([]);
    }
  }, []);

  const refreshWaStatus = useCallback(async () => {
    if (!waUseBaileys) return;
    setWaStatusLoading(true);
    try {
      const s = await whatsappBaileysAPI.getStatus();
      setWaStatus(s);
    } catch (e) {
      setWaStatus({ reachable: false, connected: false, serviceError: e.message });
    } finally {
      setWaStatusLoading(false);
    }
  }, [waUseBaileys]);

  const handleToggleWaBaileys = (v) => {
    writeUseBaileysToStorage(v);
    setWaUseBaileys(v);
    if (v) setTimeout(() => refreshWaStatus(), 0);
  };

  useEffect(() => {
    if (showModalWhatsAppAbono && waUseBaileys) refreshWaStatus();
  }, [showModalWhatsAppAbono, waUseBaileys, refreshWaStatus]);

  const seleccionarPlantillaAbono = (idStr) => {
    const id = idStr ? parseInt(idStr, 10) : NaN;
    setPlantillaSeleccionadaIdAbono(Number.isFinite(id) ? id : '');
    const p = plantillasAbono.find((x) => x.id === id);
    if (p) setMensajePersonalizadoAbono(p.texto || '');
  };

  const guardarComoNuevaAbono = async () => {
    const nombre = window.prompt('Nombre de la plantilla:', `Abono ${plantillasAbono.length + 1}`);
    if (!nombre?.trim()) return;
    try {
      const res = await whatsappTemplatesAPI.create({ nombre: nombre.trim(), texto: mensajePersonalizadoAbono });
      if (res.success) {
        toast.success('Plantilla guardada');
        await loadPlantillasAbono();
        setPlantillaSeleccionadaIdAbono(res.data.id);
      }
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar');
    }
  };

  const actualizarPlantillaAbono = async () => {
    if (!plantillaSeleccionadaIdAbono) return;
    const p = plantillasAbono.find((x) => x.id === plantillaSeleccionadaIdAbono);
    if (!p) return;
    try {
      await whatsappTemplatesAPI.update(plantillaSeleccionadaIdAbono, {
        nombre: p.nombre,
        texto: mensajePersonalizadoAbono,
      });
      toast.success('Plantilla actualizada');
      await loadPlantillasAbono();
    } catch (e) {
      toast.error(e.message || 'No se pudo actualizar');
    }
  };

  const eliminarPlantillaAbono = async () => {
    if (!plantillaSeleccionadaIdAbono) return;
    const p = plantillasAbono.find((x) => x.id === plantillaSeleccionadaIdAbono);
    if (!p || !window.confirm(`¿Eliminar la plantilla "${p.nombre}"?`)) return;
    try {
      await whatsappTemplatesAPI.delete(plantillaSeleccionadaIdAbono);
      toast.success('Plantilla eliminada');
      await loadPlantillasAbono();
      const rest = plantillasAbono.filter((x) => x.id !== plantillaSeleccionadaIdAbono);
      setPlantillaSeleccionadaIdAbono(rest[0]?.id || '');
      setMensajePersonalizadoAbono(rest[0]?.texto || '');
    } catch (e) {
      toast.error(e.message || 'No se pudo eliminar');
    }
  };

  const iniciarEnvioWhatsAppAbono = async () => {
    if (clientesAbonoConTelefono.length === 0) {
      toast.warning('Ninguno de los clientes cargados tiene teléfono.');
      return;
    }
    try {
      await assertHorarioUsoCliente();
    } catch (e) {
      toast.error(e.message);
      return;
    }
    if (waUseBaileys) {
      if (!waStatus?.reachable) {
        toast.error('No se alcanza el servicio WhatsApp.');
        return;
      }
      if (!waStatus?.connected) {
        toast.warning('WhatsApp no está conectado. Escanee el QR en el servidor.');
        return;
      }
      try {
        const items = clientesAbonoConTelefono.map((cliente) => ({
          phone: formatearTelefonoAbono(cliente.celular || cliente.telefono).replace('+', ''),
          caption: aplicarPlantillaAbono(
            cliente,
            mensajePersonalizadoAbono,
            abonoMesNombre,
            abonoAnio,
            abonoConcepto
          ),
          templateId: plantillaSeleccionadaIdAbono || undefined,
          clienteId: cliente.id,
          destinatario: cliente.razon_social,
        }));
        const { ok, fail, abortedEarly, abortMessage } = await sendWhatsAppSequential(items, {
          sendRateProfile: waSendRateProfile,
        });
        if (abortedEarly && abortMessage) {
          toast.warning(`${abortMessage} Enviados: ${ok}.`);
        } else {
          toast.success(`Enviados ${ok} mensaje(s)${fail ? `; ${fail} con error` : ''}.`);
        }
        setShowModalWhatsAppAbono(false);
        setClientesCargadosAbono([]);
        return;
      } catch (e) {
        toast.error(e.message || 'Error al enviar por Baileys');
        return;
      }
    }
    abrirWhatsappWebEscalonado(
      clientesAbonoConTelefono.map((cliente) => ({
        numero: formatearTelefonoAbono(cliente.celular || cliente.telefono).replace('+', ''),
        texto: aplicarPlantillaAbono(
          cliente,
          mensajePersonalizadoAbono,
          abonoMesNombre,
          abonoAnio,
          abonoConcepto
        ),
      })),
      1500
    );
    setShowModalWhatsAppAbono(false);
    setClientesCargadosAbono([]);
    toast.success(`Se abrirán ${clientesAbonoConTelefono.length} conversación(es) de WhatsApp.`);
  };

  const handleAnularRecibo = async () => {
    if (!detalleRecibo?.id || detalleRecibo.anulado_at) return;
    if (!window.confirm(`¿Anular el Recibo de Cobro N° ${detalleRecibo.numero_recibo}? Esta acción revierte el movimiento y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await recibosCobroAPI.anular(detalleRecibo.id);
      if (res.success) {
        toast.success(res.message || 'Recibo anulado');
        cerrarDetalle();
        loadMovimientos();
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular recibo');
    } finally {
      setAnulandoComprobante(false);
    }
  };

  const handleAnularReciboGrupo = async () => {
    if (!detalleReciboGrupo?.id || detalleReciboGrupo.anulado_at) return;
    if (!window.confirm(`¿Anular el Recibo de Cobro Grupo N° ${detalleReciboGrupo.numero_recibo}? Esta acción revierte los créditos de todos los clientes y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await recibosCobroGrupoAPI.anular(detalleReciboGrupo.id);
      if (res.success) {
        toast.success(res.message || 'Recibo de grupo anulado');
        cerrarDetalle();
        loadMovimientos();
      }
    } catch (error) {
      toast.error(error.message || 'Error al anular recibo de grupo');
    } finally {
      setAnulandoComprobante(false);
    }
  };

  const handleAnularPago = async () => {
    if (!detallePago?.id || detallePago.anulado_at) return;
    const esPagoCuenta = detallePago.cliente_id && !detallePago.proveedor_id;
    const etiqueta = esPagoCuenta ? 'Pago a cuenta' : 'Orden de Pago';
    const numero = esPagoCuenta ? detallePago.numero_pago_cuenta : detallePago.numero_pago;
    if (!window.confirm(`¿Anular el ${etiqueta} N° ${numero}? Esta acción revierte el movimiento y los fondos asociados.`)) return;
    setAnulandoComprobante(true);
    try {
      const res = await pagosAPI.anular(detallePago.id);
      if (res.success) {
        toast.success(res.message || `${etiqueta} anulada`);
        cerrarDetalle();
        loadMovimientos();
      }
    } catch (error) {
      toast.error(error.message || `Error al anular ${etiqueta.toLowerCase()}`);
    } finally {
      setAnulandoComprobante(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const ordenColumnasVisible = useMemo(
    () => ordenColumnas.filter(id => colVisible(id)),
    [ordenColumnas, columnasVisibles]
  );

  const hayFiltrosActivos = useMemo(
    () =>
      !!(filtros.fecha_desde || filtros.fecha_hasta || filtros.tipo_movimiento || filtros.movimiento_predefinido_ids?.length) ||
      (esProveedores ? !!filtros.proveedor_id : !!filtros.cliente_id),
    [filtros, esProveedores]
  );

  const movimientosOrdenados = useMemo(() => {
    if (!sortByColumn) return [...movimientos].sort(sortMovimientosCronologico);
    if (sortByColumn === 'saldo') {
      const porFecha = [...movimientos].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      let s = 0;
      const conSaldo = porFecha.map(m => {
        s += m.tipo_movimiento === 'DEBITO' ? (parseFloat(m.monto) || 0) : -(parseFloat(m.monto) || 0);
        return { ...m, _saldoParaOrdenar: s };
      });
      conSaldo.sort((a, b) => {
        const v = (a._saldoParaOrdenar || 0) - (b._saldoParaOrdenar || 0);
        return sortAsc ? v : -v;
      });
      return conSaldo;
    }
    const arr = [...movimientos];
    arr.sort((a, b) => {
      const v1 = getSortValueMov(a, sortByColumn);
      const v2 = getSortValueMov(b, sortByColumn);
      const num = typeof v1 === 'number' && typeof v2 === 'number';
      let cmp = 0;
      if (num) cmp = v1 - v2;
      else cmp = String(v1).localeCompare(String(v2), 'es');
      return sortAsc ? cmp : -cmp;
    });
    return arr;
  }, [movimientos, sortByColumn, sortAsc]);

  const movimientosConSaldoAcumulado = useMemo(() => {
    const base = saldoInicial != null ? Number(saldoInicial) : 0;
    const porFecha = [...movimientos].sort(sortMovimientosCronologico);
    const saldoPorId = {};
    let saldo = base;
    porFecha.forEach((m) => {
      saldo += deltaSaldoMovimiento(m);
      saldoPorId[m.id] = saldo;
    });
    return movimientosOrdenados.map((m) => ({ ...m, saldo_acumulado: saldoPorId[m.id] ?? base }));
  }, [movimientosOrdenados, movimientos, saldoInicial]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc(prev => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const getColumnLabel = (columnId) =>
    COLUMNAS_DEFINICION.find(c => c.id === columnId)?.label || columnId;

  const handleExportListado = async (format) => {
    try {
      await exportFromColumnas({
        filename: esProveedores ? 'movimientos_proveedores' : 'movimientos_clientes',
        sheetName: 'Movimientos',
        columnDefs: COLUMNAS_DEFINICION,
        visibleColumnIds: ordenColumnasVisible,
        items: movimientosConSaldoAcumulado,
        getCellValue: (mov, colId) => valorCeldaMovimientoExport(mov, colId, formatFecha),
        format,
      });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const generarPDF = () => {
    if (!movimientosConSaldoAcumulado.length) {
      toast.info('No hay movimientos para imprimir');
      return;
    }
    const fechaReporte = new Date();
    const fechaReporteStr = `${fechaReporte.getDate()} de ${MESES_ES[fechaReporte.getMonth()]} de ${fechaReporte.getFullYear()}`;
    const periodoHtml =
      filtros.fecha_desde || filtros.fecha_hasta
        ? `<p><strong>Período:</strong> ${filtros.fecha_desde || '...'} a ${filtros.fecha_hasta || '...'}</p>`
        : '';
    const entidadSel = esProveedores
      ? proveedores.find((p) => String(p.id) === String(filtros.proveedor_id))
      : clientes.find((c) => String(c.id) === String(filtros.cliente_id));
    const entidadInfoHtml = entidadSel
      ? `<p><strong>${esProveedores ? 'Proveedor' : 'Cliente'}:</strong> ${entidadSel.razon_social || ''}</p>
         <p><strong>CUIT:</strong> ${entidadSel.cuit || '-'}</p>`
      : `<p><strong>Listado:</strong> ${esProveedores ? 'Todos los proveedores filtrados' : 'Todos los clientes filtrados'}</p>`;
    const ultimo = [...movimientosConSaldoAcumulado].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
    const saldoFinal = ultimo?.saldo_acumulado ?? saldoFiltrado ?? saldoCuenta ?? 0;
    imprimirReporteMovimientos({
      titulo: esProveedores ? 'Movimientos de proveedores' : 'Movimientos de clientes',
      subtitulo: `Reporte generado el ${fechaReporteStr}`,
      entidadInfoHtml,
      periodoHtml,
      movimientos: movimientosConSaldoAcumulado,
      formatFecha,
      saldoFinal,
      logoUrl,
    });
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="movimientos pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>{esProveedores ? 'Movimientos Proveedores' : 'Movimientos Clientes'}</h1>
      </div>

      {esProveedores && hasPermission('gestionar_op_huerfanas') && opHuerfanasVisible && (opHuerfanasLoading || opHuerfanas.length > 0) && (
        <div className="pagina-listado-fijo">
        <div className="op-huerfanas-panel" style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: '#fff8e6',
          border: '1px solid #f0c040',
          borderRadius: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <strong>Órdenes de pago sin comprobante ({opHuerfanas.length})</strong>
            <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.85rem' }} onClick={() => setOpHuerfanasVisible(false)}>
              Ocultar
            </button>
          </div>
          {opHuerfanasLoading ? (
            <div style={{ color: '#666' }}>Cargando...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Proveedor</th>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {opHuerfanas.map((mov) => (
                    <tr key={mov.id}>
                      <td>{formatFecha(mov.fecha)}</td>
                      <td>{mov.proveedor_razon_social || '-'}</td>
                      <td title={mov.concepto}>{mov.concepto}</td>
                      <td>${parseFloat(mov.monto || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <div className="action-buttons" style={{ flexWrap: 'nowrap' }}>
                          {hasPermission('crear_debitos') && (
                            <Link
                              to="/movimientos/nuevo"
                              state={{
                                nuevoPago: true,
                                modoProveedores: true,
                                movimientoDebitoId: mov.id,
                                proveedorId: mov.proveedor_id,
                                montoHuerfano: mov.monto,
                                conceptoHuerfano: mov.concepto,
                                fechaHuerfana: mov.fecha,
                                filtrosMovimientos: { ...filtros },
                              }}
                              className="btn-edit"
                              title="Completar con formas de pago"
                            >
                              Completar OP
                            </Link>
                          )}
                          <button type="button" className="btn-delete" onClick={() => handleAnularOpHuerfana(mov)}>
                            Anular
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      )}

      <div className="listado-panel clientes-listado listado-controles">
      <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
        {filtroVisible('entidad') && (
          esProveedores ? (
            <select name="proveedor_id" value={filtros.proveedor_id} onChange={handleFilterChange}>
              <option value="">Todos los proveedores</option>
              {proveedores.map(p => (
                <option key={p.id} value={p.id}>{p.razon_social}</option>
              ))}
            </select>
          ) : (
            <select name="cliente_id" value={filtros.cliente_id} onChange={handleFilterChange}>
              <option value="">Todos los clientes</option>
              {clientes.map(c => (
                <option key={c.id} value={c.id}>{c.razon_social}</option>
              ))}
            </select>
          )
        )}
        {filtroVisible('tipo_movimiento') && (
          <select name="tipo_movimiento" value={filtros.tipo_movimiento} onChange={handleFilterChange}>
            <option value="">Todos (débito/crédito)</option>
            <option value="DEBITO">Débito</option>
            <option value="CREDITO">Crédito</option>
          </select>
        )}
        {filtroVisible('movimiento_predefinido') && (
          <FiltroPredefinidosMultiselect
            predefinidos={predefinidos}
            selectedIds={filtros.movimiento_predefinido_ids}
            onChange={(ids) => setFiltros((prev) => ({ ...prev, movimiento_predefinido_ids: ids }))}
            label="Tipo de movimiento"
          />
        )}
        {filtroVisible('fecha') && (
          <>
            <input
              type="date"
              name="fecha_desde"
              value={filtros.fecha_desde}
              onChange={handleFilterChange}
              placeholder="Fecha desde"
            />
            <input
              type="date"
              name="fecha_hasta"
              value={filtros.fecha_hasta}
              onChange={handleFilterChange}
              placeholder="Fecha hasta"
            />
          </>
        )}
        {filtroVisible('fecha') && (filtros.fecha_desde || filtros.fecha_hasta) && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={incluirSaldoInicial}
                onChange={(e) => setIncluirSaldoInicial(e.target.checked)}
              />
              <span title="Incluir saldo inicial a la fecha de inicio">Saldo inicial</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={incluirSaldoTotal}
                onChange={(e) => setIncluirSaldoTotal(e.target.checked)}
              />
              <span title="Incluir saldo total de la cuenta">Saldo de cuenta</span>
            </label>
          </>
        )}
      </div>
      <div className="filters-toolbar filters-toolbar--acciones">
          {!esProveedores && (
            <>
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{
                    ...(filtros.cliente_id ? { clienteId: Number(filtros.cliente_id) } : {}),
                    filtrosMovimientos: { ...filtros },
                  }}
                  className="btn-action-icon"
                  title="Nuevo movimiento"
                >
                  <ActionIcon name="plus" />
                </Link>
              )}
              {(hasPermission('crear_creditos') && hasPermission('crear_debitos')) && (
                <Link
                  to="/movimientos/nuevo"
                  state={{
                    nuevoRecibo: true,
                    ...(filtros.cliente_id ? { clienteId: Number(filtros.cliente_id) } : {}),
                    filtrosMovimientos: { ...filtros },
                  }}
                  className="btn-action-icon"
                  title="Nuevo recibo"
                >
                  <ActionIcon name="receipt" />
                </Link>
              )}
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{
                    nuevoPagoCliente: true,
                    ...(filtros.cliente_id ? { clienteId: Number(filtros.cliente_id) } : {}),
                    filtrosMovimientos: { ...filtros },
                  }}
                  className="btn-action-icon"
                  title="Pago a cuenta"
                >
                  <ActionIcon name="payment" />
                </Link>
              )}
              {hasPermission('crear_debitos') && hasPermission('carga_masiva') && abonoMensualPredefinido && (
                <button
                  type="button"
                  className="btn-action-icon"
                  onClick={abrirModalAbonoMensual}
                  title="Aplicar abono mensual a varios clientes"
                >
                  <ActionIcon name="calendar" />
                </button>
              )}
            </>
          )}
          {esProveedores && (
            <>
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{
                    modoProveedores: true,
                    ...(filtros.proveedor_id ? { proveedorId: Number(filtros.proveedor_id) } : {}),
                    filtrosMovimientos: { ...filtros },
                  }}
                  className="btn-action-icon"
                  title="Nuevo movimiento"
                >
                  <ActionIcon name="plus" />
                </Link>
              )}
              {hasPermission('crear_debitos') && (
                <Link
                  to="/movimientos/nuevo"
                  state={{
                    nuevoPago: true,
                    modoProveedores: true,
                    ...(filtros.proveedor_id ? { proveedorId: Number(filtros.proveedor_id) } : {}),
                    filtrosMovimientos: { ...filtros },
                  }}
                  className="btn-action-icon"
                  title="Nuevo pago a proveedor"
                >
                  <ActionIcon name="payment" />
                </Link>
              )}
            </>
          )}
          <button
            type="button"
            className="btn-action-icon"
            onClick={() => setFiltros({ ...defaultFiltrosMov })}
            title="Limpiar todos los filtros"
          >
            <ActionIcon name="clear" />
          </button>
          <SelectorColumnas
            columnas={COLUMNAS_DEFINICION}
            visibleIds={Object.keys(columnasVisibles).filter(id => columnasVisibles[id])}
            ordenColumnas={ordenColumnas}
            onOrdenChange={setOrdenColumnas}
            onToggle={(id) => setColumnasVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
            onRestaurar={() => setColumnasVisibles({ ...defaultColumnasVisiblesMov })}
            onSave={guardarPreferenciasListado}
            titulo="Columnas visibles"
            labelBoton="Columnas"
          />
          <SelectorFiltros
            filtros={FILTROS_DEFINICION_MOV}
            visibleIds={Object.keys(filtrosVisibles).filter(id => filtrosVisibles[id])}
            onToggle={(id) => setFiltrosVisibles(prev => ({ ...prev, [id]: !prev[id] }))}
            onRestaurar={() => setFiltrosVisibles({ ...defaultFiltrosVisiblesMov })}
            onSave={guardarPreferenciasListado}
            titulo="Filtros visibles"
            labelBoton="Filtros"
          />
          {movimientosConSaldoAcumulado.length > 0 && (
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDF}
              printLabel="Generar reporte PDF"
            />
          )}
      </div>

      {hayFiltrosActivos && (saldoFiltrado !== null || saldoCuenta !== null || saldoInicial !== null) && (
        <div style={{ marginBottom: 0, padding: 12, background: '#f0f4f8', borderRadius: 0, display: 'flex', gap: 24, flexWrap: 'wrap', borderBottom: '1px solid #eee' }}>
          {saldoFiltrado !== null && (
            <div>
              <strong>Saldo de los movimientos filtrados</strong>:{' '}
              <span className={saldoFiltrado > 0 ? 'negative' : saldoFiltrado < 0 ? 'positive' : ''}>
                ${Number(saldoFiltrado).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
          {saldoCuenta !== null && (esProveedores ? filtros.proveedor_id : filtros.cliente_id) && (
            <div>
              <strong>Saldo acumulado de la cuenta (sin filtros)</strong>:{' '}
              <span className={saldoCuenta > 0 ? 'negative' : saldoCuenta < 0 ? 'positive' : ''}>
                ${Number(saldoCuenta).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
          {saldoCuenta !== null && !(esProveedores ? filtros.proveedor_id : filtros.cliente_id) && (
            <div>
              <strong>Saldo acumulado {esProveedores ? 'proveedores' : 'clientes'} (sin filtros de listado)</strong>:{' '}
              <span className={saldoCuenta > 0 ? 'negative' : saldoCuenta < 0 ? 'positive' : ''}>
                ${Number(saldoCuenta).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
          {saldoInicial !== null && (
            <div>
              <strong>Saldo inicial</strong> {filtros.fecha_desde && `(al ${formatFecha(filtros.fecha_desde)}): `}
              <span className={saldoInicial > 0 ? 'negative' : saldoInicial < 0 ? 'positive' : ''}>
                ${Number(saldoInicial).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div className={`listado-tabla-area clientes-listado-tabla-area${refrescandoMovimientos ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {ordenColumnasVisible.map(colId => (
                <th
                  key={colId}
                  className="th-sortable"
                  onClick={() => handleSortColumn(colId)}
                  title={`Ordenar por ${getColumnLabel(colId)}. Clic de nuevo invierte el orden.`}
                >
                  <span>{getColumnLabel(colId)}</span>
                  {sortByColumn === colId && (
                    <span className="sort-indicator" aria-hidden="true">
                      {sortAsc ? ' ▲' : ' ▼'}
                    </span>
                  )}
                </th>
              ))}
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {movimientosConSaldoAcumulado.length === 0 ? (
              <tr>
                <td colSpan={1 + ordenColumnasVisible.length} className="no-data">No hay movimientos registrados</td>
              </tr>
            ) : (
              movimientosConSaldoAcumulado.map(mov => (
                <tr key={mov.id}>
                  {ordenColumnasVisible.map(colId => {
                    if (colId === 'fecha') {
                      return <td key={colId}>{formatFecha(mov.fecha)}</td>;
                    }
                    if (colId === 'entidad') {
                      return (
                        <td key={colId}>
                          <div style={{ fontWeight: '500' }}>{mov.razon_social}</div>
                          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{mov.cuit}</div>
                        </td>
                      );
                    }
                    if (colId === 'concepto') {
                      return (
                        <td key={colId}>
                          <MovimientoConceptoCell mov={mov} title={mov.concepto} />
                        </td>
                      );
                    }
                    if (colId === 'debito') {
                      return (
                        <td key={colId} className="negative">
                          {mov.tipo_movimiento === 'DEBITO'
                            ? <strong>${parseFloat(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                            : '-'}
                        </td>
                      );
                    }
                    if (colId === 'credito') {
                      return (
                        <td key={colId} className="positive">
                          {mov.tipo_movimiento === 'CREDITO'
                            ? <strong>${parseFloat(mov.monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                            : '-'}
                        </td>
                      );
                    }
                    if (colId === 'saldo') {
                      return (
                        <td
                          key={colId}
                          className={
                            parseFloat(mov.saldo_acumulado || 0) > 0
                              ? 'negative'
                              : parseFloat(mov.saldo_acumulado || 0) < 0
                                ? 'positive'
                                : ''
                          }
                        >
                          <strong>${parseFloat(mov.saldo_acumulado || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                        </td>
                      );
                    }
                    return <td key={colId}>-</td>;
                  })}
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => abrirDetalleMovimiento(mov)}
                        className="btn-action-icon"
                        title="Ver detalles del movimiento"
                      >
                        <ActionIcon name="search" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImprimirMovimiento(mov)}
                        className="btn-action-icon"
                        title={
                          movimientoTieneReciboGrupo(mov)
                            ? 'Imprimir recibo de cobro grupo'
                            : movimientoTieneRecibo(mov)
                            ? 'Imprimir recibo de cobro'
                            : movimientoTienePago(mov)
                              ? 'Imprimir orden de pago'
                              : 'Imprimir comprobante'
                        }
                      >
                        <ActionIcon name="print" />
                      </button>
                      {!esProveedores && (
                        <FacturaMovimientoMarca mov={mov} puedePdf={hasPermission('ver_facturacion')} />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {refrescandoMovimientos && (
        <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
          <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
          <span className="clientes-listado-refresh-label">Actualizando…</span>
        </div>
      )}
      </div>
      </div>
      </div>

      <MovimientoDetalleModal
        mov={detalleMovimiento}
        recibo={detalleRecibo}
        reciboGrupo={detalleReciboGrupo}
        pago={detallePago}
        pagoError={detallePagoError}
        loading={detalleLoading}
        esProveedores={esProveedores}
        onClose={cerrarDetalle}
        formatFecha={formatFecha}
        formatFechaHora={formatFechaHora}
        hasPermission={hasPermission}
        onAnularRecibo={handleAnularRecibo}
        onAnularReciboGrupo={handleAnularReciboGrupo}
        onAnularPago={handleAnularPago}
        anulandoComprobante={anulandoComprobante}
        logoUrl={logoUrl}
      />

      {showAbonoMensualModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!abonoMensualEnviando) {
              setShowAbonoMensualModal(false);
              setAbonoMensualPermitirDuplicado(false);
              setAbonoMensualAclaracion('');
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '520px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2>Aplicar Abono Mensual</h2>
            <p style={{ color: '#666', marginBottom: '12px' }}>
              Elija el mes y el año al que corresponde el abono. Se cargará a cada cliente su abono mensual.
              {!abonoMensualPermitirDuplicado
                ? ' Por defecto no se carga dos veces el mismo mes/año a un cliente.'
                : ' Se permitirá duplicar aunque ya exista abono de ese mes/año.'}
            </p>
            <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label>Mes *</label>
                <select
                  value={abonoMensualMes}
                  onChange={(e) => setAbonoMensualMes(parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: '10px', marginTop: '6px', fontSize: '16px' }}
                >
                  {MESES_NOMBRES.map((nombre, i) => (
                    <option key={i} value={i + 1}>{nombre}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '0 1 120px' }}>
                <label>Año *</label>
                <select
                  value={abonoMensualAnio}
                  onChange={(e) => setAbonoMensualAnio(parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: '10px', marginTop: '6px', fontSize: '16px' }}
                >
                  {(() => {
                    const anioActual = new Date().getFullYear();
                    const anos = [];
                    for (let a = anioActual - 2; a <= anioActual + 1; a++) anos.push(a);
                    return anos.map((a) => <option key={a} value={a}>{a}</option>);
                  })()}
                </select>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={abonoMensualPermitirDuplicado}
                onChange={(e) => setAbonoMensualPermitirDuplicado(e.target.checked)}
              />
              <span>Permitir duplicar abono del mismo mes/año</span>
            </label>
            <div style={{ marginBottom: '12px' }}>
              <label>Aclaración (opcional)</label>
              <input
                type="text"
                value={abonoMensualAclaracion}
                onChange={(e) => setAbonoMensualAclaracion(e.target.value)}
                placeholder="Texto a agregar al concepto"
                maxLength={200}
                style={{ width: '100%', padding: '10px', marginTop: '6px', fontSize: '16px', boxSizing: 'border-box' }}
              />
            </div>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
              Concepto que se registrará: &quot;Abono Mensual - {MESES_NOMBRES[abonoMensualMes - 1]} {abonoMensualAnio}
              {abonoMensualAclaracion.trim() ? ` - ${abonoMensualAclaracion.trim()}` : ''}&quot;.
              {abonoMensualPermitirDuplicado
                ? ' Se creará un nuevo débito aunque ya exista abono de ese mes.'
                : ' Los clientes que ya tengan abono de ese mes se omitirán.'}
            </p>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Clientes (monto según tipo)</label>
              <button type="button" className="btn-edit" style={{ fontSize: '12px' }} onClick={seleccionarTodosAbono}>
                Seleccionar todos con abono mensual &gt; 0
              </button>
            </div>
            <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
              {clientesAbonoLoading ? (
                <div className="loading">Cargando clientes...</div>
              ) : (
                clientesAbono.map((c) => (
                  <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={abonoMensualSeleccionados.includes(c.id)}
                      onChange={() => toggleClienteAbono(c.id)}
                      disabled={!(parseFloat(c.abono_mensual ?? c.valor_abono) > 0)}
                    />
                    <span style={{ flex: 1 }}>
                      {c.razon_social}
                      {c.tipo_nombre && (
                        <span style={{ color: '#666', fontSize: '12px' }}>
                          {' '}
                          — {c.tipo_nombre}: $
                          {parseFloat((c.abono_mensual ?? c.valor_abono) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </span>
                    {!(parseFloat(c.abono_mensual ?? c.valor_abono) > 0) && (
                      <span style={{ fontSize: '11px', color: '#999' }}>Sin abono mensual</span>
                    )}
                  </label>
                ))
              )}
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                className="btn-delete"
                onClick={() => {
                  setShowAbonoMensualModal(false);
                  setAbonoMensualPermitirDuplicado(false);
                  setAbonoMensualAclaracion('');
                }}
                disabled={abonoMensualEnviando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={aplicarAbonoMensual}
                disabled={abonoMensualSeleccionados.length === 0 || abonoMensualEnviando}
              >
                {abonoMensualEnviando ? 'Aplicando...' : `Aplicar a ${abonoMensualSeleccionados.length} cliente(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModalWhatsAppAbono && clientesCargadosAbono.length > 0 && (
        <div
          className="modal-overlay"
          onClick={() => setShowModalWhatsAppAbono(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '560px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <h2>Enviar aviso por WhatsApp (Abono Mensual)</h2>
            <WhatsAppServicioPanel
              useBaileys={waUseBaileys}
              onToggleUseBaileys={handleToggleWaBaileys}
              status={waStatus}
              loading={waStatusLoading}
              onRefresh={refreshWaStatus}
              sendRateProfile={waSendRateProfile}
              onSendRateProfileChange={setWaSendRateProfile}
              estimateCount={clientesAbonoConTelefono.length}
            />
            <p style={{ color: '#666', marginBottom: '12px' }}>
              Se generó el abono a <strong>{clientesCargadosAbono.length}</strong> cliente(s). Con teléfono:{' '}
              <strong>{clientesAbonoConTelefono.length}</strong>. Elija o edite la plantilla y luego inicie el envío
              {waUseBaileys ? ' (servidor).' : ' (ventanas wa.me).'}
            </p>
            <div style={{ marginBottom: '12px' }}>
              <label>Plantillas guardadas</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
                <select
                  value={plantillaSeleccionadaIdAbono}
                  onChange={(e) => seleccionarPlantillaAbono(e.target.value)}
                  style={{ padding: '8px 12px', minWidth: '180px' }}
                >
                  <option value="">(Sin selección)</option>
                  {plantillasAbono.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn-edit" onClick={guardarComoNuevaAbono}>
                  Guardar como nueva
                </button>
                <button type="button" className="btn-edit" onClick={actualizarPlantillaAbono} disabled={!plantillaSeleccionadaIdAbono}>
                  Actualizar
                </button>
                <button type="button" className="btn-delete" onClick={eliminarPlantillaAbono} disabled={!plantillaSeleccionadaIdAbono}>
                  Eliminar
                </button>
              </div>
              <PlantillaImagenEditor
                plantilla={plantillasAbono.find((p) => p.id === plantillaSeleccionadaIdAbono)}
                onChange={loadPlantillasAbono}
                useBaileys={waUseBaileys}
                canEdit={hasPermission('editar_clientes')}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="mensaje-abono">
                Mensaje (variables: {'{razon_social}'}, {'{cuit}'}, {'{telefono}'}, {'{tipo_nombre}'}, {'{mes}'}, {'{anio}'}, {'{monto}'}, {'{concepto}'})
              </label>
              <textarea
                id="mensaje-abono"
                value={mensajePersonalizadoAbono}
                onChange={(e) => setMensajePersonalizadoAbono(e.target.value)}
                rows={6}
                style={{ width: '100%', padding: '10px', marginTop: '6px', boxSizing: 'border-box' }}
              />
            </div>
            {clientesAbonoConTelefono[0] && (
              <div style={{ marginBottom: '12px', padding: '10px', background: '#f5f5f5', borderRadius: '6px' }}>
                <strong>Vista previa (primer cliente):</strong>
                <pre style={{ whiteSpace: 'pre-wrap', marginTop: '6px', fontSize: '13px' }}>
                  {aplicarPlantillaAbono(
                    clientesAbonoConTelefono[0],
                    mensajePersonalizadoAbono,
                    abonoMesNombre,
                    abonoAnio,
                    abonoConcepto
                  )}
                </pre>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                className="btn-delete"
                onClick={() => {
                  setShowModalWhatsAppAbono(false);
                  setClientesCargadosAbono([]);
                }}
              >
                Cerrar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={iniciarEnvioWhatsAppAbono}
                disabled={clientesAbonoConTelefono.length === 0}
              >
                Iniciar envío masivo ({clientesAbonoConTelefono.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Movimientos;

